import { useId, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { AccountRole } from "../../../api/accountsApi";
import { invite, type IssuedInvitation } from "../../../api/invitationsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { toUiError, type UiError } from "../../../errors/uiError";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import ModalShell from "../../../ui/overlays/ModalShell";
import { FORM_HELP_TEXT_CLASS } from "../../../ui/styles/formStyles";
import { addressesToSend } from "./emailAddresses";
import EmailAddressesField from "./EmailAddressesField";
import InvitationLink from "./InvitationLink";

interface InviteDialogProps {
  title: string;
  /** Under the addresses: whom accepting puts them into, with no approval. */
  hint: string;
  /** What every invitation carries (GS-7): its groups, a project's group carrying its project. */
  carries: { readonly groups: readonly string[]; readonly accountRole: AccountRole };
  /** Administration's choice of what it carries; nothing on a group's screen, which carries that group. */
  chooser?: ReactNode;
  /** Off while the choice can't be sent yet, such as while it loads. */
  ready?: boolean;
  /** Some invitation went out, for a list of them to read again. */
  onInvited?: () => void;
  onClose: () => void;
}

interface Refusal {
  readonly address: string;
  readonly error: UiError;
}

/**
 * Invites people by email from a group or from Administration (redesign R3-4): one or several
 * addresses, then Send; each address gets its own invitation, and accepting puts the person into
 * what it carries at once, with no approval. DMW sends no mail yet, so each acceptance link shows
 * here once for the inviter to send on. An address the server refuses stays in the field with
 * the reason; the links already shown stay too.
 */
export default function InviteDialog({ title, hint, carries, chooser, ready = true, onInvited, onClose }: InviteDialogProps) {
  const { t } = useTranslation();
  const formId = useId();
  const [addresses, setAddresses] = useState<readonly string[]>([]);
  const [typing, setTyping] = useState("");
  const [issued, setIssued] = useState<readonly IssuedInvitation[]>([]);
  const [refused, setRefused] = useState<readonly Refusal[]>([]);
  const { state, submit } = useSubmission();
  const sending = state.status === "submitting";
  const toSend = addressesToSend(addresses, typing);

  const send = () => {
    if (toSend.length === 0 || !ready) return;
    submit(async () => {
      const outcomes = await Promise.allSettled(toSend.map((email) => invite({ email, ...carries })));
      const sent = outcomes.flatMap((outcome) => (outcome.status === "fulfilled" ? [outcome.value] : []));
      const failed = toSend.flatMap((address, index) => {
        const outcome = outcomes[index];
        return outcome?.status === "rejected" ? [{ address, error: toUiError(outcome.reason) }] : [];
      });
      setIssued([...sent, ...issued]);
      setRefused(failed);
      setAddresses(failed.map((refusal) => refusal.address));
      setTyping("");
      if (sent.length > 0) onInvited?.();
    });
  };

  return (
    <ModalShell
      title={title}
      onClose={onClose}
      closeDisabled={sending}
      size="lg"
      zIndexClassName="z-[60]"
      footer={
        <div className="flex justify-end gap-2">
          <AppButton variant="secondary" size="sm" disabled={sending} onClick={onClose}>{t(issued.length === 0 ? "common.cancel" : "common.close")}</AppButton>
          <AppButton type="submit" form={formId} variant="primary" size="sm" disabled={sending || !ready || toSend.length === 0}>
            {sending ? t("login.pleaseWait") : t("access.invite.send", { count: Math.max(toSend.length, 1) })}
          </AppButton>
        </div>
      }
    >
      <form id={formId} className="space-y-3 text-sm" onSubmit={(event) => { event.preventDefault(); send(); }}>
        <EmailAddressesField addresses={addresses} typing={typing} hint={hint} disabled={sending}
          onChange={(nextAddresses, nextTyping) => { setAddresses(nextAddresses); setTyping(nextTyping); }} />
        {chooser}
        {refused.map((refusal) => (
          <div key={refusal.address}>
            <p className="font-semibold">{refusal.address}</p>
            <ErrorNotice error={refusal.error} compact={true} />
          </div>
        ))}
        {state.status === "failed" ? <ErrorNotice error={state.error} compact={true} /> : null}
        {issued.map((invitation) => <InvitationLink key={invitation.invitationId} issued={invitation} withNote={false} />)}
        {issued.length === 0 ? null : <p className={FORM_HELP_TEXT_CLASS}>{t("projects.invitation.shownOnce")}</p>}
      </form>
    </ModalShell>
  );
}
